import m from './module';
import Position from '../model/Position';
import { createError } from '../error';
import { normalizeIdentifier } from '../util';
import { INCREMENTAL_PURGE_OLD_DATA_STORE } from "../constants";

const type = 'Position';
const getArchivedPositionsForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/positions';
const editPositionUrl    = '/staff_edit_position';

const dateFieldsInLocalTimezone = [
  'claim_date',
  'start_date',
];

const dateTimeFieldsInEventTimezone = [
  'start_time',
  'end_time',
  'meal_break_start',
  'meal_break_end',
  'rest_break_start',
  'rest_break_end',
  'actual_start',
  'actual_end',
];

class PositionService {
  constructor(
    $q,
    moment,
    APIService,
    LogService,
    DataSyncService,
    IncrementalDataStoreService,
    DateTimeService,
    LanguageService,
    EventService,
    RecapResponseService,
    PositionTypeService,
    ClaimService
  ) {
    this.$q = $q;
    this.moment = moment;
    this.APIService = APIService;
    this.LogService = LogService;
    this.DataSyncService = DataSyncService;
    this.IncrementalDataStoreService = IncrementalDataStoreService;
    this.DateTimeService = DateTimeService;
    this.LanguageService = LanguageService;
    this.EventService = EventService;
    this.RecapResponseService = RecapResponseService;
    this.PositionTypeService = PositionTypeService;
    this.ClaimService = ClaimService;

    // Track what paths have been used for get multiple objects.
    // This is an ugly hack so we can later override a recap response set.
    this._usedPathsForGetMultiple = [];

    this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName = {};
    this._mostRecentDataStore = null;

    const expireIncrementalDataHandler = () => {
      return this.$q.when(this._mostRecentDataStore)
        .then(dataStore => {
          if (dataStore && dataStore.setNeedsUpdate) {
            dataStore.setNeedsUpdate();
          }
        });
    };

    // note: added "pre-refresh" hooks while debugging MOSAICPT-2127
    // not sure why we had it set up like this previously.. every time we do a sync, we would mark the data as stale
    // and cause it to require being re-downloaded
    this.DataSyncService.addPreRefreshPositionsCallback(expireIncrementalDataHandler);
    this.DataSyncService.addPreRefreshDataCallback(expireIncrementalDataHandler);
  }

  getIncrementalDataStoreForStaffingUser(staffingUser) {
    const staffingUsersId = normalizeIdentifier(staffingUser.id);
    const syncWindowDefinition = staffingUser.syncWindowDefinition;
    const dataStoreTypeKey = `Position:user=${staffingUsersId}:${syncWindowDefinition.name}`;
    if (this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey] === undefined) {
      const factory = (data, ...args) => {
        return this.create(data, ...args);
      };
      const basePath = getArchivedPositionsForStaffingUserPath
        .replace('{{staffing_users_id}}', staffingUsersId)
        .replace('{{num_days}}', '0');
      const path = this.APIService.addURLParamsFromObject(basePath, syncWindowDefinition.params);

      if (!this._usedPathsForGetMultiple.includes(path)) {
        this._usedPathsForGetMultiple.push(path);
      }
      this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey] = this.IncrementalDataStoreService.getOrBuild(dataStoreTypeKey, path, factory);
      if (INCREMENTAL_PURGE_OLD_DATA_STORE) {
        const destroyPromises = Object.keys(this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName)
          .filter(currKey => currKey !== dataStoreTypeKey)
          .map(keyToDelete => {
            delete this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[keyToDelete];
            return this.$q.when(this.IncrementalDataStoreService.destroy(keyToDelete));
          });
        if (destroyPromises.length) {
          this.$q.all(destroyPromises)
            .then(() => {
              this.LogService.debug(`PositionService purged previous data stores`);
            });
        }
      }
    }
    this._mostRecentDataStore = this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey];
    return this.$q.when(this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey]);
  }

  create(data, config = null, skipLoadingRecapResponse = false) {
    const promises = [];
    data = data || {};

    const getEventPromiseForPositionData = (data) => {
      const allowGetOrCreateEvent = true;
      if (data.event) {
        if (data.event instanceof Event) {
          return this.$q.when(data.event);
        } else if (allowGetOrCreateEvent) {
          // Call getOrCreateEvent with event data
          return this.EventService.getOrCreateWithData(data.event);
        }
      }
      if (data.events_id) {
        // Retrieve event by ID
        return this.EventService.getById(data.events_id, { force: config && config.force });
      }
      return this.$q.when(null);
    }

    promises.push(
      getEventPromiseForPositionData(data)
        .then(event => {
          data.event = event;
        })
        .catch(err => {
          this.LogService.warn(`Error getting event for position data:`, data, 'error:', err);
          return this.$q.reject(err);
        })
        .finally(() => {
          const event = data.event;

          if (!data.event) {
            // No event present
            this.LogService.warn(`PositionService.create() creating position with no event`);
          }

          // Parse dates and times
          const eventTimezoneName = event && event.timezoneName;
          if (!eventTimezoneName) {
            this.LogService.debug(`PositionService.create() no event timezone for position data`, data);
          }

          // NOTE: Previously we were parsing both date and date/time fields with event timezone. Due to MOSAICPT-997
          // we will no longer parse dates this way, but only date/times.
          const fieldsToParseWithEventTimezone = dateTimeFieldsInEventTimezone;

          // Instead, we'll parse dates without shifting timezone.
          const fieldsToParseWithLocalTimezone = dateFieldsInLocalTimezone;

          // Parse with event timezone
          for (let field of fieldsToParseWithEventTimezone) {
            if (data[field]) {
              if (typeof data[field] === 'object' && Object.keys(data[field]).length >= 1) {
                for (const property in data[field]) {
                  data[field][property] = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(data[field][property], eventTimezoneName);
                }
              } else {
                data[field] = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(data[field], eventTimezoneName);
              }
            }
          }

          // Parse with local timezone
          for (let field of fieldsToParseWithLocalTimezone) {
            if (data[field]) {
              data[field] = this.DateTimeService.parseDate(data[field]);
            }
          }

          // Ensure start_date matches start_time
          if (data.start_time) {
            data.start_date = data.start_time;
          }
        })
    );

    // Create PositionType
    if (data.position_type) {
      promises.push(
        this.PositionTypeService.create(data.position_type)
          .then(positionType => data.positionType = positionType)
      )
    }

    // Populate other users' positions if present
    if (data.other_users_positions) {
      const skipLoadingRecapResponseForOtherUser = true;
      let otherUsersPositionsPromises = data.other_users_positions.map(otherUsersPositionData => this.create(otherUsersPositionData, config, skipLoadingRecapResponseForOtherUser));
      promises.push(
        this.$q.all(otherUsersPositionsPromises)
          .then(otherUsersPositions => data.other_users_positions = otherUsersPositions)
      );
    }

    // Populate primary claim if present
    if (data.primary_claim) {
      promises.push(
        this.ClaimService.getOrCreateWithData(data.primary_claim)
          .then(primaryClaim => {
            delete data.primary_claim;
            data.primaryClaim = primaryClaim;
          })
      )
    }

    let conditionallyLoadRecapResponseForPosition = (position) => {
      let promise;
      if (!skipLoadingRecapResponse) {
         promise = this.RecapResponseService.getForPosition(position, config)
          .then(recapResponse => {
            position.recapResponse = recapResponse;
            return position;
          })
          .catch(err => {
            this.LogService.warn(`Swallowing error loading recap response for position id ${position.id} (event ${position.events_id}). Error:`, err, "Position:", position);
            return position;
          });
      } else {
        promise = this.$q.when(position);
      }
      return promise;
    };

    return this.$q
      .all(promises)
      .then(() => {
        const position = new Position(data);

        // If not present, add circular reference from primary claim pointing to this position
        if (position.primaryClaim && !position.primaryClaim.position) {
          position.primaryClaim.position = position;
        }

        return conditionallyLoadRecapResponseForPosition(position);
      })
      .catch(err => {
        this.LogService.error("Error creating position:", err);
        return this.$q.reject(err);
      });
  }

  /**
   * When recap response is saved, it gets a new ID.
   * We need to look for any positions that have a now-outdated reference to the old recap response, and replace it
   * with the new one.
   */
  updateRecapResponseReferencesForEvent(staffingUser, eventId, newRecapResponse, oldRecapResponseId) {
    if (!staffingUser) {
      return this.$q.reject(createError({
        error: 'MissingStaffingUserForUpdateRecapResponseReferencesForEvent',
        description: 'Missing staffingUser for updateRecapResponseReferencesForEvent',
      }));
    }
    eventId = normalizeIdentifier(eventId);
    const newRecapResponseId = normalizeIdentifier(newRecapResponse.id);
    const positionMatchesEventId = position => position && eventId && position.eventId === eventId;
    return this.getAllLocalForStaffingUser(staffingUser)
      .then(allPositions => {
        return allPositions;
      })
      .then(allPositions => allPositions.filter(position => {
        if (!position) {
          this.LogService.warn(`PositionService updateRecapResponseReferencesForEvent() got empty position`);
          return false;
        }
        return true;
      }))
      .then(allPositions => allPositions.filter(positionMatchesEventId))
      .then(allPositionsMatchingEvent => {
        return this.$q.all(allPositionsMatchingEvent.map(position => {
          // Check if position has recap response, matching either new or old ID
          const positionRecapResponseId = position.recapResponse && normalizeIdentifier(position.recapResponse.id);
          if (positionRecapResponseId && (positionRecapResponseId === newRecapResponseId || positionRecapResponseId === oldRecapResponseId)) {
            position.recapResponse = newRecapResponse;
            return this.$q.when(true);
          } else {
            return this.$q.when(false);
          }
        }))
      })
      .then(results => {
        return results.includes(true);
      });
  }

  getAllForStaffingUser(staffingUser, config = null) {
    return this.$q.when(this.getIncrementalDataStoreForStaffingUser(staffingUser))
      .then(dataStore => dataStore.getAll(config))
      .catch(response => {
        this.LogService.error("Error retrieving positions (PositionService.getAllForStaffingUser):)", response);
        return this.$q.reject(createError({
          error: 'ErrorRetrievingStaffingUserPositions',
          message: 'Error retrieving positions from server',
          response
        }));
      });
  }

  getByIdForStaffingUser(staffingUser, positionId, config = null) {
    return this.$q.when(this.getIncrementalDataStoreForStaffingUser(staffingUser))
      .then(dataStore => dataStore.getById(positionId, config));
  }

  getAllForStaffingUserOld(staffingUser, config = null) {
    const path = this.pathForGetAllPositionsForStaffingUser(staffingUser, config);
    if (!this._usedPathsForGetMultiple.includes(path)) {
      this._usedPathsForGetMultiple.push(path);
    }
    return this.DataSyncService.getMultipleObjects(type, path, data => this.create(data, config), config)
      .catch(response => {
        this.LogService.error("Error retrieving positions (PositionService.getAllForStaffingUser):", response);
        return this.$q.reject(createError({
          error: 'ErrorRetrievingStaffingUserPositions',
          message: 'Error retrieving positions from server',
          response,
        }));
      });
  }

  pathForGetAllPositionsForStaffingUser(staffingUser, config = null) {
    const staffingUserId = staffingUser.id;
    const syncWindowDefinition = staffingUser.syncWindowDefinition;
    const basePath = getArchivedPositionsForStaffingUserPath.replace('{{staffing_users_id}}', staffingUserId).replace('{{num_days}}', 0);
    const path = this.APIService.addURLParamsFromObject(basePath, syncWindowDefinition.params);
    if (!this._usedPathsForGetMultiple.includes(path)) {
      this._usedPathsForGetMultiple.push(path);
    }
    return path;
  }

  getAllLocalForStaffingUser(staffingUser) {
    return this.$q.when(this.getIncrementalDataStoreForStaffingUser(staffingUser))
      .then(store => store.getAllLocal());
  }

  getLocalById(id) {
    return this.$q.when(this._mostRecentDataStore)
      .then(dataStore => {
        if (!dataStore) {
          return null;
        }
        return dataStore.getLocalById(id);
      });
  }

  translatedNameForPosition(position) {
    if (position && position.positionType) {
      return this.LanguageService.translateProperty(position.positionType, 'name');
    } else if (position && position.name) {
      return position.name;
    } else {
      return '';
    }
  }

  serialize(position) {
    return {
      ...position
    };
  }

  updateTimesForPositionId(positionId, newStartTime, newEndTime) {
    return this.getLocalById(positionId)
      .then(localPosition => {
        if (localPosition) {
          return localPosition;
        } else {
          this.LogService.warn(`PositionService.updateTimesForPositionId(${positionId}) couldn't find local position; trying getById()`);
          return this.getById(positionId);
        }
      })
      .then(position => {
        if (!position) {
          const err = this.$q.reject(createError({
            message: 'Error retrieving position data for update',
            error: 'UnableToRetreivePosition',
          }));
          return this.$q.reject(err);
        }
        let positionClone = {...position};
        delete positionClone.event.recapDefinition
        const payload = {
          // position_id: positionId,
          date: this.moment(newStartTime).format('MM/DD/YYYY'),
          start_time: this.DateTimeService.serializeDateTimeForAPI(newStartTime),
          end_time: this.DateTimeService.serializeDateTimeForAPI(newEndTime),
          event: positionClone.event,
          venue: {
            vpid: position.event.vpid,
          },
          event_type: position.event.event_type,
          name: position.event.name,
          description: position.event.additional_event_notes,
        };
        this.LogService.debug(`PositionService.updateTimesForPositionId(${positionId}) posting to API:`, payload);
        return this.APIService.post(editPositionUrl, payload);
      })
      .then(response => {
        return response;
      })
      .catch(err => {
        this.LogService.error("Error updating position times:", err);
        return this.$q.reject(err);
      });
  }
}

export default PositionService;

m.service('PositionService', PositionService);
